import React, { useState, useEffect } from 'react'

export default function GalleryLightbox({ gallerySlug, fallbackImages = [], title = "Galleri" }) {
  const [images, setImages] = useState(fallbackImages)
  const [activeIdx, setActiveIdx] = useState(null)

  useEffect(() => {
    if (gallerySlug) {
      // Fetch gallery from CMS by gallery_slug or slug
      fetch(`https://api.dev.auxo.dk/public/v2/content/galleri`)
        .then(res => res.json())
        .then(data => {
          if (data && data.items && data.items.length > 0) {
            const match = data.items.find(
              item => item.gallery_slug === gallerySlug || item.slug?.da === gallerySlug
            )
            if (match && match.images && match.images.length > 0) {
              setImages(match.images.map((img, i) => ({
                src: img,
                alt: `${title} ${i + 1}`
              })))
            }
          }
        })
        .catch(err => console.error('Error fetching gallery:', err))
    }
  }, [gallerySlug, title])

  if (!images || images.length === 0) return null

  const openLightbox = (idx) => setActiveIdx(idx)
  const closeLightbox = () => setActiveIdx(null)

  const prevImage = (e) => {
    e.stopPropagation()
    setActiveIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const nextImage = (e) => {
    e.stopPropagation()
    setActiveIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  return (
    <div className="my-8">
      {title && <h3 className="text-xl font-bold text-slate-900 mb-4">{title}</h3>}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {images.map((img, idx) => {
          const src = typeof img === 'string' ? img : img.src
          const alt = typeof img === 'string' ? `${title} ${idx + 1}` : (img.alt || `${title} ${idx + 1}`)

          return (
            <button
              key={idx}
              type="button"
              onClick={() => openLightbox(idx)}
              className="group relative aspect-square bg-slate-100 rounded-lg overflow-hidden border border-slate-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-600"
            >
              <img
                src={src}
                alt={alt}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl" aria-hidden="true" />
              </div>
            </button>
          )
        })}
      </div>

      {/* Lightbox Modal */}
      {activeIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xs flex items-center justify-center p-4 select-none"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white text-3xl p-2 hover:text-red-400 transition-colors z-50 cursor-pointer"
            aria-label="Luk visning"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>

          {/* Prev Button */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white text-xl p-4 rounded-full transition-colors z-50 cursor-pointer"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true" />
            </button>
          )}

          {/* Current Image */}
          <div className="max-w-4xl max-h-[85vh] flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={typeof images[activeIdx] === 'string' ? images[activeIdx] : images[activeIdx].src}
              alt={typeof images[activeIdx] === 'string' ? title : images[activeIdx].alt}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
            />
            <p className="text-white/80 text-xs mt-3">
              {activeIdx + 1} / {images.length}
            </p>
          </div>

          {/* Next Button */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white text-xl p-4 rounded-full transition-colors z-50 cursor-pointer"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
